import { Controller } from "@hotwired/stimulus";

const MAX_FILE_SIZE = 10 * 1024 * 1024;

export default class extends Controller {
  static targets = [
    "recordTab",
    "uploadTab",
    "recordPanel",
    "uploadPanel",
    "fileInput",
    "source",
    "signedId",
    "preview",
    "previewPlayer",
    "error",
    "caption",
    "submit",
  ];

  static values = {
    micDeniedMessage: String,
    durationErrorMessage: String,
    fileTooLargeMessage: String,
    noFileMessage: String,
    hasExistingAudio: { type: Boolean, default: false },
  };

  connect() {
    this.activate("record");
  }

  disconnect() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
  }

  activateRecord() {
    this.activate("record");
  }

  activateUpload() {
    this.activate("upload");
  }

  recorderUploaded(event) {
    const { signedId, blob } = event.detail;
    this.signedIdTarget.value = signedId;
    this.fileInputTarget.value = "";
    this.sourceTarget.value = "recorded";
    this.showPreview(blob);
  }

  recorderError(event) {
    this.showError(event.detail?.message || "");
  }

  recorderUnsupported() {
    if (!this.hasRecordTabTarget) return;
    this.recordTabTarget.classList.add("hidden");
    this.activate("upload");
  }

  async fileSelected() {
    this.clearError();
    const file = this.fileInputTarget.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      this.showError(this.fileTooLargeMessageValue);
      this.fileInputTarget.value = "";
      return;
    }

    this.signedIdTarget.value = "";
    this.sourceTarget.value = "uploaded";
    this.showPreview(file);
  }

  submit(event) {
    this.clearError();

    if (
      !this.signedIdTarget.value &&
      !this.fileInputTarget.files[0] &&
      !this.hasExistingAudioValue
    ) {
      event.preventDefault();
      this.showError(this.noFileMessageValue);
    }
  }

  activate(mode) {
    const recording = mode === "record";
    this.recordPanelTarget.classList.toggle("hidden", !recording);
    this.uploadPanelTarget.classList.toggle("hidden", recording);
    this.recordTabTarget.classList.toggle("border-brand-500", recording);
    this.recordTabTarget.classList.toggle("text-brand-600", recording);
    this.recordTabTarget.classList.toggle("text-content-muted", !recording);
    this.recordTabTarget.setAttribute("aria-selected", recording.toString());
    this.uploadTabTarget.classList.toggle("border-brand-500", !recording);
    this.uploadTabTarget.classList.toggle("text-brand-600", !recording);
    this.uploadTabTarget.classList.toggle("text-content-muted", recording);
    this.uploadTabTarget.setAttribute("aria-selected", (!recording).toString());
    this.sourceTarget.value = recording ? "recorded" : "uploaded";
    this.resetState();
  }

  resetState() {
    this.clearError();
    this.signedIdTarget.value = "";
    this.fileInputTarget.value = "";
    this.previewTarget.classList.add("hidden");
  }

  showPreview(file) {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = URL.createObjectURL(file);
    this.previewTarget.classList.remove("hidden");

    const player = this.previewPlayerTarget;
    const controller = this.application.getControllerForElementAndIdentifier(
      player,
      "audio-player",
    );
    controller?.setSource(this.previewUrl, this.captionTarget.value);
  }

  showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
  }

  clearError() {
    this.errorTarget.textContent = "";
    this.errorTarget.classList.add("hidden");
  }
}
